import { createSlice } from "@reduxjs/toolkit";
import {
  getMerchantList,
  getTerminalList,
  deleteTerminal,
  getTerminalDetails,
  sendMerchantModifyBilling,
  sendMerchantModifyWalletConfirmation,
} from "./Merchant.thunk";

const baseState = {
  screenMode: "general",
  merchantList: [],
  terminalList: [],
  terminalDetails: null,
  terminalInfo: null,
  merchantID: null,
  merchantName: null,
  merchantStore: null,
  reqhashMAC: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  terminalDetailStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  merchantModifyBillingStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  merchantModifyWalletConfirmationStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  merchantModifyBillingError: null,
  merchantModifyWalletConfirmationError: null,
  readOnlyForm: false,
  formContent: null,
  formType: null, // 'add' | 'edit' (terminals)
  page: 1,
  pageSize: 5,
  totalRecords: 0,
};

const initialState = {
  ...baseState,
};

export const merchantListSlice = createSlice({
  name: "merchantList",
  initialState,
  reducers: {
    ResetStore: (state) => {
      state.status = "idle";
      state.terminalDetailStatus = "idle";
      state.error = null;
      state.merchantList = [];
      state.reqhashMAC = null;
      state.screenMode = "general";
      state.merchantID = null;
      state.merchantName = null;
      state.merchantStore = null;
      state.terminalList = [];
      state.terminalDetails = null;
      state.reqhashMAC = null;
      state.readOnlyForm = false;
      state.formContent = null;
      state.formType = null;
      state.page = 1;
      state.pageSize = 5;
      state.totalRecords = 0;
    },
    resetStoreItem: (state) => {
      state.screenMode = "general";
      state.merchantID = null;
      state.merchantName = null;
      state.merchantStore = null;
      state.terminalList = [];
      state.terminalDetails = null;
      state.terminalDetailStatus = "idle";
      state.status = "idle";
      state.error = null;
      state.readOnlyForm = false;
      state.formContent = null;
      state.formType = null;
    },
    setScreenMode: (state, action) => {
      state.screenMode = action.payload;
    },
    setReadOnlyForm: (state, action) => {
      state.readOnlyForm = action.payload;
    },
    setFormContent: (state, action) => {
      state.formContent = action.payload;
    },
    setFormType: (state, action) => {
      state.formType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMerchantList.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getMerchantList.fulfilled, (state, action) => {
        const { merchantList, reqhashMAC, page, page_size, total_records } =
          action.payload;
        state.merchantList = merchantList;
        state.reqhashMAC = reqhashMAC;
        state.screenMode = "general";
        state.merchantID = null;
        state.merchantName = null;
        state.merchantStore = null;
        state.terminalList = [];
        state.status = "succeeded";
        state.error = null;
        state.page = page;
        state.pageSize = page_size;
        state.totalRecords = total_records;
      })
      .addCase(getMerchantList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Error getting merchant list";
        state.page = 1;
        state.pageSize = 5;
        state.totalRecords = 0;
        state.merchantList = [];
      });
    builder
      .addCase(getTerminalList.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getTerminalList.fulfilled, (state, action) => {
        const { terminalList, merchantID, merchantName, merchantStore } =
          action.payload;
        state.merchantID = merchantID;
        state.merchantName = merchantName;
        state.merchantStore = merchantStore;
        state.terminalList = terminalList;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(getTerminalList.rejected, (state, action) => {
        state.status = "failed";
        state.merchantID = null;
        state.merchantName = null;
        state.merchantStore = null;
        state.terminalList = [];
        state.reqhashMAC = null;
        state.error = action.payload?.message || "Error getting terminal list";
        state.screenMode = "general";
      });
    builder
      .addCase(deleteTerminal.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteTerminal.fulfilled, (state) => {
        // state.status = "succeeded";
        state.error = null;
      })
      .addCase(deleteTerminal.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Error deleting terminal";
      });
    builder
      .addCase(getTerminalDetails.pending, (state) => {
        state.terminalDetailStatus = "loading";
        state.error = null;
      })
      .addCase(getTerminalDetails.fulfilled, (state, action) => {
        const { terminalDetails, terminalInfo } = action.payload;
        state.terminalDetails = terminalDetails || null;
        state.terminalInfo = terminalInfo || null;
        state.terminalDetailStatus = "succeeded";
        state.error = null;
      })
      .addCase(getTerminalDetails.rejected, (state, action) => {
        state.terminalDetailStatus = "failed";
        state.error =
          action.payload?.message || "Error getting terminal details";
      });
    builder
      .addCase(sendMerchantModifyBilling.pending, (state) => {
        state.merchantModifyBillingStatus = "loading";
        state.merchantModifyBillingError = null;
      })
      .addCase(sendMerchantModifyBilling.fulfilled, (state, action) => {
        state.merchantModifyBillingStatus = "succeeded";
        state.merchantModifyBillingError = null;
      })
      .addCase(sendMerchantModifyBilling.rejected, (state, action) => {
        state.merchantModifyBillingStatus = "failed";
        state.merchantModifyBillingError =
          action.payload?.message || "Error sending merchant modify billing";
      });
    builder
      .addCase(sendMerchantModifyWalletConfirmation.pending, (state) => {
        state.merchantModifyWalletConfirmationStatus = "loading";
        state.merchantModifyWalletConfirmationError = null;
      })
      .addCase(
        sendMerchantModifyWalletConfirmation.fulfilled,
        (state, action) => {
          state.merchantModifyWalletConfirmationStatus = "succeeded";
          state.merchantModifyWalletConfirmationError = null;
        }
      )
      .addCase(
        sendMerchantModifyWalletConfirmation.rejected,
        (state, action) => {
          state.merchantModifyWalletConfirmationStatus = "failed";
          state.merchantModifyWalletConfirmationError =
            action.payload?.message ||
            "Error sending merchant modify wallet confirmation";
        }
      );
  },
});

export const {
  ResetStore,
  setScreenMode,
  resetStoreItem,
  setReadOnlyForm,
  setFormContent,
  setFormType,
} = merchantListSlice.actions;

export default merchantListSlice.reducer;
